import React, { useState, useEffect, useContext } from 'react';
import getSuitCSSClasses from '@/utils/getSuitCSSClasses';
import { useI18nContext, Trans } from '@ecg-marktplaats/js-react-i18n';
import { BasicModalWithFooter, StandAloneLink, RowHeader, SegmentedControl } from '@hz-design-system/web-ui';

import InfoTooltip from '@/client/components/FilterSidebar/atoms/InfoTooltip';
import { linkGeneratorHelper, generateLinkObject } from '@/utils/links/linkGeneratorHelper';
import getClientPageQueryObject from '@/client/utils/getClientPageQueryObject';
import pushToRouter from '@/client/utils/pushToRouter';

import experiments from '@/utils/labs/experiments';
import CATEGORIES_L1 from '@/constants/categories';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import {
  PRICE_TYPE_LEASE_ATTRIBUTE_VALUE_ID,
  PRICE_TYPE_SALE_ATTRIBUTE_VALUE_ID,
} from '@/constants/attributes/nonStandards';
import hasBothPriceTypes from '../utils/priceTypeHelper';

const LOCAL_STORAGE_KEY_DEFAULT_PRICE_TYPE = 'DefaultPriceType';
const LOCAL_STORAGE_KEY_PRICE_TYPE_SEEN = 'InfoTooltipDefaultPriceTypeSeen';

type TAttributeToggleGroupFacetProps = {
  label: string;
  attributeGroup: TAttributeValue[];
  inputKey: string;
  searchRequestObject: TSearchRequestObject;
  preventScroll: boolean;
};

const isAllPriceType = (attr?: TAttributeValue[]) => hasBothPriceTypes(attr) || !attr;

const search = (
  inputKey: string,
  attributeValue: TAttributeValue | undefined,
  attributeGroup: TAttributeValue[],
  searchRequestObject: TSearchRequestObject,
  withAllAttributes: boolean,
) => {
  const attributes = searchRequestObject.attributes;
  delete attributes[inputKey];

  if (!attributeValue) {
    const saleAttr = attributeGroup.find((a) => a.attributeValueId === PRICE_TYPE_SALE_ATTRIBUTE_VALUE_ID);
    const leaseAttr = attributeGroup.find((a) => a.attributeValueId === PRICE_TYPE_LEASE_ATTRIBUTE_VALUE_ID);
    const both = [saleAttr, leaseAttr].filter(Boolean) as TAttributeValue[];
    if (both.length) {
      attributes[inputKey] = both;
    }
  } else {
    attributes[inputKey] = [attributeValue];
  }

  const linkOptions = { searchRequestObject, attributes, withAllAttributes };
  const href = linkGeneratorHelper(linkOptions);
  const pageQuery = getClientPageQueryObject(generateLinkObject(linkOptions));
  pushToRouter({ query: pageQuery, href });
};

const setDefaultPriceType = (attributeValueId: number) => {
  localStorage.setItem(LOCAL_STORAGE_KEY_DEFAULT_PRICE_TYPE, JSON.stringify(attributeValueId));
};

const AttributeToggleGroupFacet: React.FC<TAttributeToggleGroupFacetProps> = ({
  label,
  attributeGroup,
  inputKey,
  searchRequestObject,
  preventScroll,
}) => {
  const { t } = useI18nContext();
  const {
    labsConfig,
    tenantContext: { tenant },
  } = useContext(EnvironmentContext);
  const { isFetchResultsOnceEnabled, isFetchResultsOnceAllCatEnabled, isFetchResultsHIOnceEnabled } = experiments({
    labsConfig,
  });
  const withAllAttributes =
    isFetchResultsOnceAllCatEnabled ||
    (isFetchResultsOnceEnabled && searchRequestObject.categories?.l1Category?.id === CATEGORIES_L1.DAMES_KLEREN) ||
    (isFetchResultsHIOnceEnabled &&
      tenant === 'mp' &&
      searchRequestObject.categories?.l1Category?.id === CATEGORIES_L1.HUIS_EN_INRICHTING);
  const [leaseModalOpen, setLeaseModalOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<TAttributeValue | undefined>(
    attributeGroup.filter(({ selected }) => selected)?.[0],
  );
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const title = t(`facets.${inputKey}`, label);
  const priceTypeAttr = searchRequestObject?.attributes?.[inputKey];
  const isAllActive = !selectedValue && isAllPriceType(priceTypeAttr);

  // SINGLE authoritative sync effect
  useEffect(() => {
    // ALL state when BOTH present
    if (hasBothPriceTypes(priceTypeAttr)) {
      if (selectedValue !== undefined) setSelectedValue(undefined);
      return;
    }

    // Explicit single selection from request
    if (Array.isArray(priceTypeAttr) && priceTypeAttr.length === 1) {
      const id = priceTypeAttr[0].attributeValueId;
      if (!selectedValue || selectedValue.attributeValueId !== id) {
        const fromGroup = attributeGroup.find((a) => a.attributeValueId === id);
        setSelectedValue(fromGroup || priceTypeAttr[0]);
      }
      return;
    }

    // If no priceType in request but attributeGroup marks one selected (initial load)
    if (!priceTypeAttr) {
      const first = attributeGroup.find((a) => a.selected);
      if (first && (!selectedValue || selectedValue.attributeValueId !== first.attributeValueId)) {
        setSelectedValue(first);
      }
    }
  }, [searchRequestObject, attributeGroup, inputKey, selectedValue, priceTypeAttr]);

  useEffect(() => {
    if (hasBothPriceTypes(priceTypeAttr)) return;
    if (selectedValue) {
      setShowInfoTooltip(false);

      try {
        localStorage.setItem(LOCAL_STORAGE_KEY_PRICE_TYPE_SEEN, JSON.stringify(true));
      } catch {}
      return;
    }

    try {
      const storageItemDefault = localStorage.getItem(LOCAL_STORAGE_KEY_DEFAULT_PRICE_TYPE);
      const storageItemTooltip = localStorage.getItem(LOCAL_STORAGE_KEY_PRICE_TYPE_SEEN);
      if (storageItemDefault) {
        const userDefaultPriceType = JSON.parse(storageItemDefault);
        const defaultAttribute = attributeGroup.find(
          ({ attributeValueId }) => attributeValueId === userDefaultPriceType,
        );
        if (defaultAttribute) {
          setSelectedValue(defaultAttribute);
          search(inputKey, defaultAttribute, attributeGroup, searchRequestObject, withAllAttributes);
        }
      } else if (!storageItemTooltip) {
        setShowInfoTooltip(true);
      }
    } catch {}
  }, [selectedValue, attributeGroup, inputKey, searchRequestObject, withAllAttributes, priceTypeAttr]);

  const onCloseTooltip = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY_PRICE_TYPE_SEEN, JSON.stringify(true));
  };

  const handleOnClick = (value: string) => {
    localStorage.setItem(LOCAL_STORAGE_KEY_PRICE_TYPE_SEEN, JSON.stringify(true));
    setShowInfoTooltip(false);

    const attributeValue = value ? attributeGroup.find((a) => String(a.attributeValueId) === value) : undefined;

    if (!attributeValue) {
      localStorage.removeItem(LOCAL_STORAGE_KEY_DEFAULT_PRICE_TYPE);
      setSelectedValue(undefined);
    } else {
      if (attributeValue.attributeValueId === PRICE_TYPE_SALE_ATTRIBUTE_VALUE_ID) {
        setDefaultPriceType(attributeValue.attributeValueId);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY_DEFAULT_PRICE_TYPE);
      }
      setSelectedValue(attributeValue);
    }

    if (preventScroll) {
      window.preventScroll = true;
    }

    search(inputKey, attributeValue, attributeGroup, searchRequestObject, withAllAttributes);
  };

  return (
    <div className={getSuitCSSClasses({ namespace: 'hz', name: 'Filter' })}>
      <div className={getSuitCSSClasses({ namespace: 'hz', name: 'Filter-headingSection' })}>
        <RowHeader title={title} />
        <StandAloneLink className="ModalTrigger" onClick={() => setLeaseModalOpen(!leaseModalOpen)}>
          {t('readMore.linkText')}
        </StandAloneLink>
      </div>
      <SegmentedControl
        ariaLabel={title}
        options={[
          { value: '', label: t('facets.all') },
          ...attributeGroup.map((attributeValue) => ({
            value: String(attributeValue.attributeValueId),
            label: t(`facets.${attributeValue.attributeValueKey}`, attributeValue.attributeValueLabel || ''),
          })),
        ]}
        value={isAllActive ? '' : String(selectedValue?.attributeValueId ?? '')}
        onChange={handleOnClick}
      />
      {showInfoTooltip && <InfoTooltip helpTextKey="facets.help.defaultPriceType" onCloseTooltip={onCloseTooltip} />}
      {leaseModalOpen && (
        <BasicModalWithFooter
          size="m"
          title={t('search.pricetype.modal_title')}
          className="PriceTypeModal"
          onClose={() => setLeaseModalOpen(false)}
          buttons={[
            {
              component: 'PrimaryButton',
              text: t('search.pricetype.modal_close'),
              onClick: () => setLeaseModalOpen(false),
            },
          ]}
        >
          <Trans
            i18nKey="search.pricetype.modal"
            paragraph-open={Trans.safeString('<p>')}
            paragraph-close={Trans.safeString('</p>')}
            title-open={Trans.safeString('<h5>')}
            title-close={Trans.safeString('</h5>')}
            bold-open={Trans.safeString('<div class="hz-Text hz-Text--bodyLargeStrong">')}
            bold-close={Trans.safeString('</div>')}
            list-open={Trans.safeString('<ul>')}
            list-close={Trans.safeString('</ul>')}
            list-item-open={Trans.safeString('<li>')}
            list-item-close={Trans.safeString('</li>')}
            break={Trans.safeString('<br>')}
            background-open={Trans.safeString('<div class="hz-backgroundBox">')}
            background-close={Trans.safeString('</div>')}
          />
        </BasicModalWithFooter>
      )}
    </div>
  );
};

export default AttributeToggleGroupFacet;
